// src/features/inventory/pages/EditItemPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useInventoryItems } from '../hooks/useInventoryItems';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { FormPage, FormSection, FormRow, FormActions } from '../../../components/common/form';

export default function EditItemPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { showSuccess, showError } = useSnackbar();

  const {
    currentItem,
    loading,
    error,
    clearError,
    fetchItemById,
    updateItem,
  } = useInventoryItems();

  const [form, setForm] = useState({ name: '', description: '', uom: '' });
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  useEffect(() => {
    if (itemId && !currentItem) {
      fetchItemById(itemId);
    }
  }, [itemId]);

  useEffect(() => {
    if (currentItem && !initialDataLoaded) {
      setForm({
        name: currentItem.name ?? '',
        description: currentItem.description ?? '',
        uom: currentItem.uom ?? '',
      });
      setInitialDataLoaded(true);
    }
  }, [currentItem, initialDataLoaded]);

  const handleChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const result = await updateItem(itemId, {
      name: form.name.trim(),
      description: form.description.trim() || null,
      uom: form.uom.trim(),
    });
    if (result.success) {
      showSuccess(t('inventory:items.success.updated'));
      navigateBack('..', { replace: true });
    } else {
      showError(result.error);
    }
  }, [updateItem, itemId, form, showSuccess, showError, navigateBack, t]);

  const handleCancel = useCallback(() => {
    navigateBack('..', { replace: true });
  }, [navigateBack]);

  if (loading && !currentItem) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!currentItem && !loading) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <IconButton onClick={() => navigateBack('..', { relative: 'path' })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">{t('inventory:items.edit_form.title')}</Typography>
        </Box>
        <Alert severity="error">{t('inventory:items.not_found')}</Alert>
      </Box>
    );
  }

  return (
    <FormPage
      title={t('inventory:items.edit_form.title')}
      subtitle={currentItem ? t('inventory:items.edit_form.subtitle', { name: currentItem.name }) : undefined}
      crumb={currentItem?.name}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      error={error}
      onErrorClose={clearError}
    >
      {initialDataLoaded && (
        <>
          <FormSection>
            <FormRow widths={[3, 1]}>
              <TextField
                label={t('inventory:items.create_form.name')}
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label={t('inventory:items.create_form.uom')}
                value={form.uom}
                onChange={(e) => handleChange('uom', e.target.value)}
                fullWidth
                required
                slotProps={{ htmlInput: { maxLength: 20 } }}
              />
            </FormRow>
            <TextField
              label={t('inventory:items.create_form.description')}
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              multiline
              rows={4}
              slotProps={{ htmlInput: { maxLength: 1000 } }}
            />
          </FormSection>
          <FormActions
            onCancel={handleCancel}
            submitType="submit"
            submitIcon={<SaveIcon />}
            loading={loading}
          />
        </>
      )}
    </FormPage>
  );
}
